import { useMemo, useState } from "react";
import clsx from "clsx";

import classes from "./styles.module.scss";

import type { IAlertProps } from "@components/molecules/alert";
import { VAlertVariants } from "@components/molecules/alert/variants";
import { Modal } from "@components/atoms/modal";
import { Typography } from "@components/atoms/typography";
import { closeAlert } from "@/stores/uiStore";
import { InformationAlertButtons } from "@components/molecules/alert/informationAlertButtons";
import { ActionAlertButtons } from "@components/molecules/alert/actionAlertButtons";
import { DeleteAlertButtons } from "@components/molecules/alert/deleteAlertButtons";

/**
 * Alert component that displays various types of alerts.
 * @param {IAlertProps} props - The props for the Alert component.
 * @param {boolean} props.open - Indicates whether the alert is open or closed.
 * @param {string} props.title - The title of the alert.
 * @param {string} props.content - The content of the alert.
 * @param {IAlertType} props.type - The type of the alert (information, action, delete).
 * @param {function} props.onBeforeClose - A function to be called before closing the alert.
 * @param {string} props.closeTitle - The title for the close button.
 * @param {string} props.cancelTitle - The title for the cancel button (for action and delete alerts).
 * @param {function} props.onCancel - A function to handle the cancel action (for action and delete alerts).
 * @param {string} props.approveTitle - The title for the approve button (for action and delete alerts).
 * @param {function} props.onApprove - A function to handle the approve action (for action and delete alerts).
 * @returns Alert - The rendered Alert component.
 */
export function Alert(props: IAlertProps) {
  const {
    open,
    title,
    content,
    type,
    onBeforeClose,
    closeTitle,
    cancelTitle,
    onCancel,
    approveTitle,
    onApprove,
    children,
    noButtons,
    customIcon,
  } = props;

  const { icon } = VAlertVariants[type];

  const [isLoading, setIsLoading] = useState(false);

  // Determine the appropriate alert buttons based on the alert type
  const alertButtons = useMemo(() => {
    return {
      information: (
        // Render buttons for an information alert
        <InformationAlertButtons
          onClose={() => {
            closeAlert();
          }}
          closeTitle={closeTitle}
          onBeforeClose={onBeforeClose}
        />
      ),
      action: (
        // Render buttons for an action alert
        <ActionAlertButtons
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onClose={() => {
            closeAlert();
          }}
          approveTitle={approveTitle}
          cancelTitle={cancelTitle}
          onApprove={onApprove}
          onCancel={onCancel}
        />
      ),
      delete: (
        // Render buttons for a delete alert
        <DeleteAlertButtons
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onClose={() => {
            closeAlert();
          }}
          approveTitle={approveTitle}
          cancelTitle={cancelTitle}
          onApprove={onApprove}
          onCancel={onCancel}
        />
      ),
    }[type];
  }, [
    approveTitle,
    cancelTitle,
    closeTitle,
    isLoading,
    onApprove,
    onBeforeClose,
    onCancel,
    type,
  ]);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (isLoading) return;
        if (onBeforeClose) {
          onBeforeClose();
        }
        closeAlert();
      }}
      backdrop
      draggable={false}
    >
      <div className={clsx(classes.alert)}>
        <div className={clsx(classes.alert__contents)}>
          <Typography variant={"h5"} color={"primary-main"}>
            <div className={clsx(classes.alert__head)}>
              <div className={"d-inline-flex"}>{customIcon || icon}</div>
              <div>{title}</div>
            </div>
          </Typography>
          {content && <Typography>{content}</Typography>}
        </div>
        {children}
        {!noButtons && (
          <div className={clsx(classes.alert__buttons)}>
            {/* Render the appropriate alert buttons */}
            {alertButtons}
          </div>
        )}
      </div>
    </Modal>
  );
}
